import { FC } from "react";
import cx from "classnames";
import styles from "./basic-info.module.scss";
import { formatNumber } from "../../../utils/stringFormatter";
import { IVDPBlockData, IVDPPageBlock, VDPBlockPosition } from "../../../types/vdp";
import { getTranslationModule } from "../../../utils/translations";
import VDPSection from "@/ui/page-components/vehicle-details-page/vdp-section";
import { ColorTypeField, getBackgroundValue, getColorType } from "@/utils/colors";

const BasicInfo: FC<IVDPPageBlock> = ({ vdpGlobalData, translations }) => {
  const t_common = getTranslationModule(translations, "common");
  const t_vdp = getTranslationModule(translations, "vdp");
  const vehicle = vdpGlobalData.vehicle;

  return (
    <VDPSection title={t_vdp("basic_info")} subtitle={"Mileage, Year, Exterior Color, Etc."}>
      <div className={styles.info}>
        <ul className={styles.list}>
          {vehicle?.mileage ? (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/mileage.svg"} alt="mileage" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("mileage")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue}>{`${formatNumber(vehicle?.mileage || 0)} mi`}</span>
            </li>
          ) : null}
          {vehicle?.year ? (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/year.svg"} alt="year" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("year")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.year.toString()}>
                {vehicle?.year}
              </span>
            </li>
          ) : null}
          {vehicle?.drivetrain && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/drivetrain.svg"} alt="drivetrain" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("drivetrain")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.drivetrain}>
                {vehicle?.drivetrain}
              </span>
            </li>
          )}
          {vehicle?.engine && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/engine.svg"} alt="engine" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("engine")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.engine}>
                {vehicle?.engine}
              </span>
            </li>
          )}
          {vehicle?.transmission && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/transmission.svg"} alt="transmission" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("transmission")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.transmission}>
                {vehicle?.transmission}
              </span>
            </li>
          )}
          {vehicle?.cityMPG && vehicle?.highwayMPG && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/mpg.svg"} alt="mpg" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("MPG")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span
                className={styles.itemValue}
                title={`${vehicle?.cityMPG}/${vehicle?.highwayMPG}`}
              >{`${vehicle?.cityMPG}/${vehicle?.highwayMPG}`}</span>
            </li>
          )}
        </ul>
        <ul className={styles.list}>
          {vehicle?.fuelType && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/fueltype.svg"} alt="fueltype" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("fuelType")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.fuelType}>
                {vehicle?.fuelType}
              </span>
            </li>
          )}
          {vehicle?.extColor && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/exterior.svg"} alt="exterior" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("extColor")}</span>
                <span className={styles.dashed}></span>
              </div>
              <div className={styles.itemColor}>
                <span className={styles.itemValue} title={vehicle?.extColor}>
                  {vehicle?.extColor}
                </span>
                {vehicle.color && getColorType(vehicle.color) && (
                  <span
                    className={styles.colorField}
                    style={{ background: getBackgroundValue(ColorTypeField.PROPERTIES, getColorType(vehicle.color)) }}
                  ></span>
                )}
              </div>
            </li>
          )}
          {vehicle?.intColor && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/interior.svg"} alt="interior" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("intColor")}</span>
                <span className={styles.dashed}></span>
              </div>
              <div className={styles.itemColor}>
                <span className={styles.itemValue} title={vehicle?.intColor}>
                  {vehicle?.intColor}
                </span>
                {vehicle.colorInternal && (
                  <span
                    className={cx(styles.colorField, styles.circle)}
                    style={{
                      background: getBackgroundValue(ColorTypeField.PROPERTIES, vehicle.colorInternal),
                    }}
                  ></span>
                )}
              </div>
            </li>
          )}
          {vehicle?.vin && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/vin.svg"} alt="vin" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("vin")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.vin}>
                {vehicle?.vin}
              </span>
            </li>
          )}
          {vehicle?.stock && (
            <li className={styles.item}>
              <div className={styles.iconContainer}>
                <img src={"/images/basic-info/stock.svg"} alt="stock" />
              </div>
              <div className={styles.itemContainer}>
                <span className={styles.itemTitle}>{t_common("stock")}</span>
                <span className={styles.dashed}></span>
              </div>
              <span className={styles.itemValue} title={vehicle?.stock}>
                {vehicle?.stock}
              </span>
            </li>
          )}
        </ul>
      </div>
    </VDPSection>
  );
};

const VDPBlockData: IVDPBlockData = {
  component: (props: IVDPPageBlock, key: any) => (
    <BasicInfo
      key={key}
      config={props.config}
      siteData={props.siteData}
      pageContext={props.pageContext}
      renderingPosition={props.renderingPosition}
      vdpGlobalData={props.vdpGlobalData}
      translations={props.translations}
    />
  ),
  position: VDPBlockPosition.LEFT,
};

export default VDPBlockData;
